import {
  CHART_SECTION_STATUS,
  CHART_TYPE,
  type ChartSectionStatus,
  type ChartType,
  type IBasicChartConfigurations,
  type IChartCardInfo,
  type ICompletionStatus,
} from '../models';

/** The "My profile" chart summaries shown on the dashboard. `null` = not loaded or failed. */
export interface DashboardChartSummaries {
  companyProfileSummary: IBasicChartConfigurations | null;
  ddqSummary: IBasicChartConfigurations | null;
  kyc: IBasicChartConfigurations | null;
  kpi: IBasicChartConfigurations | null;
  policyFiles: IBasicChartConfigurations | null;
  reportingSummary: IBasicChartConfigurations | null;
  investorProfile: IBasicChartConfigurations | null;
  pmKpiMyProfile: IBasicChartConfigurations | null;
}

export const EMPTY_CHART_SUMMARIES: DashboardChartSummaries = {
  companyProfileSummary: null,
  ddqSummary: null,
  kyc: null,
  kpi: null,
  policyFiles: null,
  reportingSummary: null,
  investorProfile: null,
  pmKpiMyProfile: null,
};

type ChartStatus = 'expired' | 'requested' | 'draft';

interface IChartConfig {
  summaryKey: keyof DashboardChartSummaries;
  /** Only statuses with a message produce an info card. */
  messages: Partial<Record<ChartStatus, string>>;
}

const CHART_CONFIG: Record<ChartType, IChartConfig> = {
  [CHART_TYPE.PROFILE]: {
    summaryKey: 'companyProfileSummary',
    messages: {
      expired: '<span class="text-warning">Review your entity details</span>',
      requested: '<span class="text-danger">Fill in your entity details</span>',
      draft: '<span class="text-danger">Submit your entity details</span>',
    },
  },
  [CHART_TYPE.DDQ]: {
    summaryKey: 'ddqSummary',
    messages: {
      expired: '<span class="text-warning">Renew your questionnaire</span>',
      requested: '<span class="text-danger">Submit your questionnaire</span>',
    },
  },
  [CHART_TYPE.FILES]: {
    summaryKey: 'policyFiles',
    messages: {
      expired: '<span class="text-warning">Update all mandatory files</span>',
      requested: '<span class="text-danger">Upload all mandatory files</span>',
    },
  },
  [CHART_TYPE.KYC]: {
    summaryKey: 'kyc',
    messages: {
      requested: '<span class="text-danger">Provide ownership & control information</span>',
    },
  },
  [CHART_TYPE.KPI]: {
    summaryKey: 'kpi',
    messages: {
      expired: '<span class="text-warning">Update your KPI report</span>',
      requested: '<span class="text-danger">Submit your KPI report</span>',
    },
  },
  [CHART_TYPE.DFT]: {
    summaryKey: 'reportingSummary',
    messages: {
      expired: '<span class="text-warning">Share your DFT report with counterparties</span>',
      requested: '<span class="text-danger">Submit your DFT report</span>',
    },
  },
  [CHART_TYPE.INVESTOR]: {
    summaryKey: 'investorProfile',
    messages: {
      expired: '<span class="text-warning">Update information</span>',
      requested: '<span class="text-danger">Submit information</span>',
    },
  },
  [CHART_TYPE.PM_KPI]: {
    summaryKey: 'pmKpiMyProfile',
    messages: {
      expired: '<span class="text-warning">Update your PM KPI report</span>',
      requested: '<span class="text-danger">Complete PM KPI report</span>',
    },
  },
};

// Order is the display priority: an expired section wins over a requested one, then a draft.
const STATUS_PRIORITY: readonly {
  status: ChartStatus;
  banner: ChartSectionStatus;
  decoration: ChartSectionStatus;
}[] = [
  {
    status: 'expired',
    banner: CHART_SECTION_STATUS.REQUESTED_UPDATE,
    decoration: CHART_SECTION_STATUS.REQUESTED_UPDATE,
  },
  {
    status: 'requested',
    banner: CHART_SECTION_STATUS.REQUESTED,
    decoration: CHART_SECTION_STATUS.INCOMPLETE,
  },
  { status: 'draft', banner: CHART_SECTION_STATUS.DRAFT, decoration: CHART_SECTION_STATUS.DRAFT },
];

/** Description card of a "My profile" chart, derived from its summary banner. */
export function getChartCardInfo(type: ChartType, charts: DashboardChartSummaries): IChartCardInfo {
  const config: IChartConfig | undefined = CHART_CONFIG[type];
  const banner = config ? charts[config.summaryKey]?.banner : undefined;

  for (const { status, banner: expected, decoration } of STATUS_PRIORITY) {
    const description = config?.messages[status];
    if (banner === expected && description) {
      return { icon: 'info_outline', description, isShowInfo: true, decoration };
    }
  }

  if (config && banner === CHART_SECTION_STATUS.COMPLETED) {
    return {
      icon: 'check',
      description: 'All information is up to date.',
      isShowInfo: true,
      decoration: CHART_SECTION_STATUS.COMPLETED,
    };
  }

  return {
    icon: 'check',
    description: '',
    isShowInfo: false,
    decoration: CHART_SECTION_STATUS.NOT_AVAILABLE,
  };
}

export function getCompletionStatus(
  charts: Pick<DashboardChartSummaries, 'kyc' | 'kpi' | 'policyFiles'>
): ICompletionStatus {
  return {
    isKYCComplete: charts.kyc?.banner === CHART_SECTION_STATUS.COMPLETED,
    isKPIComplete:
      charts.kpi?.banner === CHART_SECTION_STATUS.COMPLETED || !charts.kpi?.isDisplayChart,
    isFilesComplete: charts.policyFiles?.banner === CHART_SECTION_STATUS.COMPLETED,
  };
}
